<script setup lang="ts">
/**
 * Draws the animated illustration a questionnaire carries in the CMS. It began as a proof of concept
 * — whether the design system's animated illustrations could drive the assessment card, and whether
 * the same path holds when the file comes from the CMS rather than the bundle — and both are settled:
 * the five illustrations are in Directus and the tile draws them from there.
 *
 * Why the markup is fetched and inlined rather than hung in an `<img>`: an SVG behind an `<img>` is
 * a closed document. Its own animation runs, but nothing outside can start or stop it, and the
 * design asks for a second animation that fires under the pointer and resets when it leaves. Inline,
 * the drawing is part of the page, and the design system's own trigger — a `ji-playing` class on an
 * ancestor — is all it takes. Same reasoning the repo already records for `svg-illustration.vue`.
 *
 * The markup is sanitised because the eventual source is a file someone uploads to Directus, and
 * inserting a stranger's markup into our own page is the textbook injection route. DOMPurify's SVG
 * profile keeps the drawing, its `<style>` block and its filters, and drops script and event
 * handlers. The bundled file goes through the same filter on purpose: the point is to prove the path
 * we would actually ship, not a shortcut that only works for our own assets.
 *
 * That profile also drops SMIL outright, which costs the resting tremble: it is `<animate>` on a
 * turbulence seed, so a filtered file holds a frozen distortion instead of a moving one. SMIL is
 * dropped for a reason — it can rewrite an attribute after sanitising — so it comes back narrowly:
 * two elements, and only where what they animate is on a list of harmless targets. Measured against
 * this version of DOMPurify rather than assumed (`today-assessment-art.test.ts` pins all of it):
 *
 * - `<set>` stays banned: `<set attributeName="onmouseover">` survives the filter untouched.
 * - `<animate>` aimed at `href` is neutralised by DOMPurify itself, but aimed at an event handler it
 *   is NOT, which is what the allow-list below is for.
 */
import DOMPurify from 'dompurify';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {TODAY_ASSESSMENT_ART_MOTION} from '@/ts/utils/today-assessment-art-motion';
import {scopeSvgStyles} from '@/ts/composables/pure/use-svg-style-scope';
import {getRandomId} from '@/ts/utils/pure-functions';

const emit = defineEmits<{
  /**
   * The drawing is not going to appear: no file where the CMS said, or the browser refused to fetch
   * it. Said out loud so the card can fall back to the questionnaire's picture rather than leave a
   * hole — which is what makes a deployment without the download permission harmless rather than
   * broken.
   */
  unavailable: [];
}>();

const props = defineProps<{
  /** Where the drawing lives: a bundled asset URL, or a CMS one (through the dev proxy). */
  src: string;
  /** Whether the card is under the pointer. Owned by the card, so the whole card is the trigger. */
  playing: boolean;
  /** Whether this is the card the slider is showing. An unseen drawing has nothing to animate for. */
  onScreen?: boolean;
  /**
   * Bumped every time the card arrives on screen. A counter rather than a flag because arriving is
   * an event, not a state: a card that stays on screen would otherwise hold the animation open and
   * leave the pointer nothing to trigger — which is exactly what a flag did here.
   */
  arrivals?: number;
}>();

/**
 * What an animation element is allowed to drive. An allow-list rather than a block-list: the drawing
 * only ever animates geometry and filter parameters, so anything else is either a mistake or an
 * attack, and neither should reach the page.
 */
const ANIMATABLE = new Set([
  // filters
  'seed',
  'baseFrequency',
  'scale',
  // paint
  'opacity',
  'offset',
  'stop-color',
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-opacity',
  'stroke-dashoffset',
  'stroke-width',
  // geometry
  'transform',
  'd',
  'points',
  'x',
  'y',
  'x1',
  'x2',
  'y1',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'width',
  'height',
]);

/**
 * The class the design system's own animations wait for. Their export writes `playing` on the
 * `<svg>` root instead, which matches nothing — that is why a downloaded file only trembles. We put
 * the right one on the wrapper, which is what their site does too, so the file works unedited.
 */
const PLAYING_CLASS = 'ji-playing';

/**
 * This element's own class, which every rule the drawing brings is confined to. Per instance, not
 * shared: with one class for all of them, a file's `svg { … }` rule reaches the drawings of the other
 * cards on screen — the page-wide leak again, one step smaller.
 */
const SCOPE_CLASS = `assessment-art-${getRandomId()}`;

/**
 * The pointer has gone and the drawing is finishing its pass. It still carries the trigger class, so
 * it is still moving; what it no longer does is repeat.
 */
const isSettling = ref(false);

/** Bumped by every trigger, so a wait that has been overtaken knows to keep its hands off. */
let generation = 0;

/** Longer than any pass the design system's pieces declare (the longest is 4.2s). */
const LONGEST_PASS_MS = 5000;

let settleTimer = 0;


/**
 * Repeating belongs to the pointer, not to arriving: a card plays its animation once when it comes
 * on screen and keeps going only while someone is actually pointing at it. Otherwise a tile nobody
 * is touching would animate forever, which is the "funfair" the design explicitly does not want.
 */
const loops = computed<boolean>(() =>
  TODAY_ASSESSMENT_ART_MOTION.loopWhileHovered && props.playing && !isSettling.value);
const wobbles = computed<boolean>(() => TODAY_ASSESSMENT_ART_MOTION.restAnimation);

const logger = useLogger();

const markup = ref<string | null>(null);
const wrapper = ref<HTMLElement | null>(null);
const isPlaying = ref(false);

let frame = 0;

const prefersReducedMotion = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The resting wobble is not gated on any class: it is drawn with SMIL and runs from the moment the
 * drawing is on screen. Holding it still therefore means stopping the SVG's own clock rather than
 * withholding a class — which is what both the switch and the reader's own preference need.
 *
 * Optional call because the clock is a browser API the test environment does not implement; without
 * the guard the whole load fails there and the drawing silently disappears.
 */
const holdStill = (): void => {
  const svg = wrapper.value?.querySelector('svg');
  // Off screen counts as a reason to hold: the slider mounts every card at once, so without this the
  // turbulence filter of four drawings nobody can see keeps running for as long as the page is open.
  if (wobbles.value && Boolean(props.onScreen) && !prefersReducedMotion()) {
    svg?.unpauseAnimations?.();
    return;
  }
  svg?.pauseAnimations?.();
};

/**
 * The drawing brings its own stylesheet, and inlined that is a stylesheet of the page: one of the
 * design system's files says `svg { … }`, which reached every SVG the app renders — the slider
 * arrows first. Confining it is delicate enough (an at-rule's body holds steps, not selectors) to
 * live and be tested on its own.
 */
function scopeStyles(holder: HTMLElement, renamedIds: ReadonlyMap<string, string>): void {
  holder.querySelectorAll('style').forEach((style) => {
    style.textContent = scopeSvgStyles(
      style.textContent ?? '',
      `.${SCOPE_CLASS}`,
      `.${PLAYING_CLASS}`,
      renamedIds,
    );
  });
}

/**
 * Every identifier the drawing carries, renamed to belong to this instance, and every reference to
 * one renamed with it.
 *
 * An SVG's ids live in the page's one id space once it is inlined, so two cards drawn from files
 * that both call their blur `#filter0` end up with two elements answering to the same name. The
 * document resolves `url(#filter0)` to whichever came first, and the second drawing quietly borrows
 * the first one's filter, gradient or clip — a bug that only shows when more than one card is on
 * screen, which is every card in this slider.
 *
 * References travel in three shapes and all three are rewritten: `url(#id)`, wherever it appears
 * (a presentation attribute, an inline style, the drawing's own stylesheet), `href="#id"` on an
 * animation, and the `id.event` syntax SMIL uses to start one animation off another. Only names the
 * drawing actually defines are rewritten, so a reference to something outside is left exactly as it
 * was rather than silently repointed.
 *
 * `<use>` is not among them, and cannot be: DOMPurify's SVG profile deletes the element outright,
 * both spellings of its href. Measured against the pinned version, not assumed. So a drawing that
 * reuses shapes that way loses them, and loses them quietly, since the check above only asks whether
 * a drawing survived at all. Nothing the five files ship does this; restoring `<use>` means also
 * bounding it to same-document fragments, which is a security change of its own.
 */
const NAMESPACED_REFERENCE_ATTRIBUTES = new Set(['href', 'xlink:href', 'begin', 'end']);
function escapeForRegExp(value: string): string {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/gu, String.raw`\$&`);
}

/**
 * `$` is special on the REPLACEMENT side too (`$&`, `` $` ``, `$<name>`), and an id may legally
 * contain one. Unescaped, `url(#a$&b)` came out as `url(#sc-aurl(#a$&b)b)` — verified — because the
 * replacement quoted the whole match back into itself.
 */
function escapeReplacement(value: string): string {
  return value.replaceAll('$', '$$$$');
}
function namespaceIds(holder: HTMLElement): ReadonlyMap<string, string> {
  const renamed = new Map<string, string>();
  holder.querySelectorAll('[id]').forEach((node) => {
    const id = node.getAttribute('id') ?? '';
    if (id !== '') {
      renamed.set(id, `${SCOPE_CLASS}-${id}`);
      node.setAttribute('id', `${SCOPE_CLASS}-${id}`);
    }
  });
  if (renamed.size === 0) {
    return renamed;
  }
  const rewrite = (text: string): string => {
    let result = text;
    renamed.forEach((to, from) => {
      const name = escapeForRegExp(from);
      const target = escapeReplacement(to);
      result = result
        // `URL(` too: CSS function names are case-insensitive, and one written in capitals kept
        // pointing at the pre-rename id.
        .replaceAll(new RegExp(String.raw`url\((?<lead>\s*['"]?)#${name}(?<tail>['"]?\s*)\)`, 'giu'), `url($<lead>#${target}$<tail>)`)
        // No bare `#id` here on purpose: `#abc` is a valid hex colour as well as a valid id selector,
        // and over a whole stylesheet the two are indistinguishable — `fill: #abc` was rewritten into
        // an invalid colour and the rule silently dropped, while `fill:#abc` survived, so the spacing
        // decided it. Id SELECTORS are renamed inside the CSS walk instead (`scopeSvgStyles` takes the
        // map), which sees a prelude apart from a declaration and so needs no boundary guessing at
        // all. `url(#…)` stays here: that one is unambiguous wherever it appears.
        // The `id.event` syntax SMIL starts one animation with. Not case-folded: ids are
        // case-sensitive, unlike the function name above.
        .replaceAll(new RegExp(String.raw`(?<before>^|[\s;])${name}(?=\.[a-zA-Z])`, 'gu'), `$<before>${target}`);
    });
    return result;
  };
  holder.querySelectorAll('*').forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const isReference = NAMESPACED_REFERENCE_ATTRIBUTES.has(attribute.name) || attribute.name === 'style';
      if (attribute.name !== 'id' && (isReference || attribute.value.includes('url('))) {
        node.setAttribute(attribute.name, rewrite(attribute.value));
      }
    });
  });
  holder.querySelectorAll('style').forEach((style) => {
    style.textContent = rewrite(style.textContent ?? '');
  });
  return renamed;
}

/**
 * The same collision, one level up: `@keyframes` names are global too, so two drawings that both
 * name their movement `float` leave the second definition answering for both. Confining the rules to
 * this instance does not help, since a rule reaches its keyframes by name and not by selector.
 *
 * The rename covers the at-rule and the declarations that call it, both `animation-name` and the
 * `animation` shorthand — where the name is matched as a whole word, so a timing function or a
 * duration that happens to read the same is left alone.
 *
 * Names are collected across every `<style>` first and then rewritten everywhere, `style` attributes
 * included: a file that defines its keyframes in one block and calls them from another, or from an
 * inline declaration, had the definition renamed and the call left behind — the animation then names
 * something that no longer exists and simply never runs.
 *
 * Only the at-rule keyword folds case. A keyframes name is a custom identifier and case-sensitive, so
 * `@keyframes Fade` and `@keyframes fade` are two animations, and folding them collapsed both onto
 * one name and lost the other.
 */
function namespaceKeyframes(holder: HTMLElement): void {
  const styles = Array.from(holder.querySelectorAll('style'));
  const names = new Set<string>();
  styles.forEach((style) => {
    Array.from((style.textContent ?? '').matchAll(/@(?:-[a-z]+-)?keyframes\s+(?<name>[\w-]+)/giu))
      .forEach((match) => {
        const name = match.groups?.name ?? '';
        if (name !== '') {
          names.add(name);
        }
      });
  });
  if (names.size === 0) {
    return;
  }
  const rewrite = (css: string): string => {
    let result = css;
    names.forEach((name) => {
      const escaped = escapeForRegExp(name);
      const target = escapeReplacement(`${SCOPE_CLASS}-${name}`);
      result = result
        .replaceAll(
          new RegExp(String.raw`(?<at>@(?:-[a-z]+-)?keyframes\s+)${escaped}(?![\w-])`, 'gu'),
          `$<at>${target}`,
        )
        .replaceAll(
          new RegExp(String.raw`(?<declaration>animation(?:-name)?\s*:[^;}]*?)(?<![\w-])${escaped}(?![\w-])`, 'gu'),
          `$<declaration>${target}`,
        );
    });
    return result;
  };
  styles.forEach((style) => {
    style.textContent = rewrite(style.textContent ?? '');
  });
  holder.querySelectorAll('[style]').forEach((node) => {
    node.setAttribute('style', rewrite(node.getAttribute('style') ?? ''));
  });
}

/**
 * Turns what the sanitiser returned into markup fit to insert, or throws.
 *
 * The throw matters as much as the cleaning. A 200 that is not an SVG — a CMS error payload, a proxy
 * login page — loses its tags to the profile and keeps its text, and that text would be painted onto
 * the card. A file that sanitises to nothing would leave the card with no drawing, no picture and no
 * glyph, because a component that renders nothing says nothing either.
 *
 * The animation filter is a second pass over what DOMPurify returned rather than a hook inside it,
 * because hooks are global to the library: one registered here would silently change how every other
 * piece of CMS content in the app is filtered.
 */
const acceptArtwork = (sanitised: string): string => {
  const holder = document.createElement('div');
  holder.innerHTML = sanitised;
  if (holder.querySelector('svg') === null) {
    throw new Error('the file does not hold a drawing');
  }
  // Renamed before the styles are confined, because the walk needs the map to rewrite the id
  // selectors it finds in a prelude.
  const renamedIds = namespaceIds(holder);
  scopeStyles(holder, renamedIds);
  namespaceKeyframes(holder);
  // Every animation element the profile can return, not the two we happen to add back: DOMPurify
  // also keeps `animateMotion` and `animateColor`, both of which carry an arbitrary `attributeName`
  // straight through — measured against the pinned version. Neither is a working exploit today
  // (`animateMotion` ignores the attribute by spec, `animateColor` left the engines years ago), but
  // the boundary this list draws should hold as written.
  //
  // The test is the attribute, never the tag name: `animateMotion` legitimately names nothing at all,
  // because it animates along a path, so a rule of "drop what is not on the list" would delete it
  // every time — closing the hole by breaking the feature.
  holder.querySelectorAll('animate, animateTransform, animateMotion, animateColor').forEach((node) => {
    const driven = node.getAttribute('attributeName');
    if (driven !== null && !ANIMATABLE.has(driven)) {
      node.remove();
    }
  });
  return holder.innerHTML;
};

/**
 * Taking the class off and putting it back in the same frame does not restart a CSS animation — the
 * browser never sees it gone. Two frames apart, it does. This is what makes the hover replay on a
 * second pass instead of firing once and staying put.
 */
const replay = (): void => {
  cancelAnimationFrame(frame);
  generation++;
  isSettling.value = false;
  isPlaying.value = false;
  if (prefersReducedMotion()) {
    return;
  }
  frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => {
      isPlaying.value = true;
    });
  });
};

/** Bumped per fetch, so a slow answer for an old drawing cannot speak for the current one. */
let loadGeneration = 0;

/** The frame the load waits for before holding the drawing still, cancellable on its own. */
let loadFrame = 0;

/**
 * How long a drawing may take before the card gives up on it and shows the picture instead.
 *
 * Without a deadline the only failure the chain answered was a refusal: a CMS that accepts the
 * connection and never replies left the art area empty until the browser's own timeout, minutes
 * later, with the picture never taking its place. Generous enough not to punish a slow connection —
 * these are a few kB — and short enough that a card is never blank for long.
 */
const LOAD_DEADLINE_MS = 8000;

/**
 * Throws unless the answer says it is a drawing.
 *
 * Asked of the answer rather than inferred from what survives sanitising: a branded error page or a
 * proxy interstitial routinely carries an inline logo, so it holds an `<svg>`, passes a shape check,
 * and paints its heading onto the card. Directus serves `image/svg+xml`.
 */
const SVG_MEDIA_TYPE = 'image/svg+xml';
function refuseUnlessSvg(response: Response): void {
  const type = response.headers.get('content-type') ?? '';
  if (!type.startsWith(SVG_MEDIA_TYPE)) {
    throw new Error(`served as ${type === '' ? 'nothing' : type} rather than an SVG`);
  }
}
const load = async(src: string): Promise<void> => {
  const mine = ++loadGeneration;
  markup.value = null;
  const giveUp = new AbortController();
  const deadline = window.setTimeout(() => {
    giveUp.abort();
  }, LOAD_DEADLINE_MS);
  try {
    const response = await fetch(src, {signal: giveUp.signal});
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    refuseUnlessSvg(response);
    const accepted = acceptArtwork(DOMPurify.sanitize(await response.text(), {
      /* eslint-disable @typescript-eslint/naming-convention -- DOMPurify API shape */
      USE_PROFILES: {svg: true, svgFilters: true},
      ADD_TAGS: ['animate', 'animateTransform'],
      ADD_ATTR: ['calcMode', 'keyTimes', 'keySplines', 'begin', 'repeatDur', 'from', 'to', 'by'],
      // A drawing needs neither, and the profile allows both: `<svg tabindex="0"><a href="…">` comes
      // through verbatim, which puts a tab stop with a destination of the uploader's choosing inside
      // a wrapper marked `aria-hidden` — axe's `aria-hidden-focus`, and unreachable by anything that
      // announces itself. None of the five drawings uses either.
      FORBID_TAGS: ['a'],
      FORBID_ATTR: ['tabindex'],
      /* eslint-enable @typescript-eslint/naming-convention */
    }));
    // Checked BEFORE the write, not after: guarded afterwards, a late answer for a previous drawing
    // had already painted itself over the current one, and an answer arriving after unmount still
    // wrote to a component that was gone.
    if (mine !== loadGeneration) {
      return;
    }
    markup.value = accepted;
    // Its own handle: sharing one with the replay chain meant a pointer arriving inside this single
    // frame cancelled the rest of the load, so the drawing was never held still or played.
    await new Promise((resolve) => {
      loadFrame = requestAnimationFrame(resolve);
    });
    // Asked again after the frame: `src` can change inside it, and a continuation that has been
    // overtaken would then replay for a drawing that is no longer on the card — bumping the shared
    // generation and cutting short a settle that was waiting on it.
    if (mine !== loadGeneration) {
      return;
    }
    holdStill();
    // The card that is already on screen when its drawing lands has nothing to arrive later: without
    // this, the three pieces that wait for the trigger sit still until the pointer finds them.
    if (props.playing || (props.arrivals ?? 0) > 0) {
      replay();
    }
  } catch (error) {
    if (mine !== loadGeneration) {
      return;
    }
    logger.warn(`Could not load the assessment artwork from ${src}: ${String(error)}`)();
    markup.value = null;
    emit('unavailable');
  } finally {
    window.clearTimeout(deadline);
  }
};

/**
 * Leaving lets the pass in flight finish instead of cutting it: the loop class comes off, so the
 * animation stops repeating, and the trigger class stays until nothing is running any more. A drawing
 * frozen mid-stride reads as a glitch; one that completes reads as an answer.
 *
 * The generation guards the wait: a pointer that comes back while we are waiting starts a replay, and
 * this continuation must not then undo it.
 */
const settle = async(): Promise<void> => {
  cancelAnimationFrame(frame);
  window.clearTimeout(settleTimer);
  isSettling.value = true;
  const mine = ++generation;

  // Asked for after the repeat has actually come off the element: read in the same tick, an infinite
  // animation is still infinite, and its `finished` either never arrives or arrives at once.
  await nextTick();
  const running = wrapper.value?.getAnimations?.({subtree: true}) ?? [];
  const finished = Promise.allSettled(running.map(async(animation) => animation.finished));
  // With a ceiling, because the wait is only as trustworthy as the file: a drawing whose own rule
  // says `infinite` never finishes, and without this the card would animate for the rest of the
  // session and every later departure would find the previous one still waiting.
  // The id is this departure's own. Held in a shared variable, the first departure's continuation
  // cleared the second one's timer, and two departures inside the ceiling left the card pinned open
  // for the rest of the session.
  let timer = 0;
  const ceiling = new Promise((resolve) => {
    timer = window.setTimeout(resolve, LONGEST_PASS_MS);
    settleTimer = timer;
  });
  void Promise.race([finished, ceiling]).then(() => {
    window.clearTimeout(timer);
    if (generation === mine) {
      isPlaying.value = false;
      isSettling.value = false;
    }
  });
};

watch(() => props.src, load, {immediate: true});

// The switches are read when a drawing loads: they are build-time configuration, not state, so there
// is nothing to watch. What does change while the page is open is whether the card is on screen.
watch(() => props.onScreen, holdStill);

watch(() => props.playing, (playing) => {
  if (playing) {
    replay();
  } else {
    void settle();
  }
});

// Arriving replays too, and only replays: the pointer decides whether it then keeps going.
watch(() => props.arrivals, () => {
  replay();
});

onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  cancelAnimationFrame(loadFrame);
  window.clearTimeout(settleTimer);
  // Nothing pending may write to a component that is going away.
  generation++;
  loadGeneration++;
});
</script>

<template>
  <!-- eslint-disable vue/no-v-html -- sanitised above with DOMPurify's SVG profile; v-safe-html's
       HTML profile would strip the drawing itself, which is the same exception svg-illustration.vue
       documents for bundled assets -->
  <div
    v-if="markup"
    ref="wrapper"
    class="assessment-art"
    :class="{
      [SCOPE_CLASS]: true,
      [PLAYING_CLASS]: isPlaying,
      'assessment-art--loop': loops,
    }"
    aria-hidden="true"
    v-html="markup"
  />
  <!-- eslint-enable vue/no-v-html -->
</template>

<style scoped lang="scss">
.assessment-art {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  min-height: 0;
}

/**
 * A reader who asked for less motion gets none, whatever the drawing says. Withholding the trigger
 * class and stopping the SVG's own clock covers the pieces that wait for us and the SMIL wobble, and
 * leaves out exactly the ones that need it most: two of the five animate on their own with plain CSS
 * and answer nothing. This is what actually holds them.
 */
@media (prefers-reduced-motion: reduce) {
  .assessment-art :deep(*) {
    animation: none !important;
    transition: none !important;
  }
}

/**
 * Looping is done by overriding the iteration count rather than by restarting the animation when it
 * ends: the file's own rule sets it to one inside a shorthand, and a single declaration from here
 * beats it without having to know the names of a piece's moving parts.
 *
 * `!important` because confining the file's stylesheet to this component (`scopeSvgStyles`) also
 * raised its specificity to exactly ours, and a tie goes to whichever comes later in the document —
 * which is the drawing's own `<style>`, injected inside this element. Measured: without it, the three
 * pieces gated on the trigger class play once and only the self-starting one repeats.
 */
.assessment-art--loop.ji-playing :deep(*) {
  animation-iteration-count: infinite !important;
}

/**
 * A square box, which is what the design draws: 283px measured on a 600px card, with the drawing letterboxed
 * inside it by its own aspect ratio — that is why the taller pieces read smaller than the wide ones,
 * and matching the square is what keeps them all the size the design intends.
 *
 * Overflow stays visible, again as the design has it: a piece that travels — the chair rolls 68px
 * out of its own viewBox — would otherwise be cut at the edge of a box it never fills.
 */
.assessment-art :deep(svg) {
  display: block;
  width: min(100%, 283px);
  aspect-ratio: 1;
  height: auto;
  overflow: visible;
}
</style>
